import { Injectable, NgZone } from '@angular/core';
import { ChecklistModel } from '@fi-app/checklist/checklist.model';
import { BehaviorSubject } from 'rxjs';
import { PublicationType } from '@fi-shared/types/pubication-type';
import { SideListOptionType } from '@fi-shared/types/side-list-option-type';
import { ChecklistService } from '@fi-app/checklist/checklist.service';
import { ActionType, ObjectTypes } from '@fi-shared/types';
import { fiDebounce } from '@fi-shared/utils/debounce';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { FiSignalRHelperService } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { FiItemUpdatedEventType } from '@fi-shared/fi-signal-r/fi-item-update-event-type';

@Injectable()
export class ChecklistsReminderService {
    checkListRemindersCount$ = new BehaviorSubject<number>(0);

    private _checkListReminders$ = new BehaviorSubject<ChecklistModel[]>([]);
    get checkListReminders$() {
        return this._checkListReminders$.asObservable();
    }
    get checkListReminders() {
        return this._checkListReminders$.value;
    }

    private remindersChecklists: ChecklistModel[] = [];

    constructor(
        private checklistService: ChecklistService,
        private ngZone: NgZone,
        private fiSignalRHelperService: FiSignalRHelperService,
        private fiSignalRService: FiSignalRService,
    ) {
        this.fiSignalRHelperService.initialRemindersChecklists.subscribe((result) => {
            this.remindersChecklists = <any>result;
            this.filterRemindersData();
        });
    }

    initCheckListsForSystemNotifications() {
        /*var promise = this.checklistService.getCheckLists(this.getParameterForSystemNotifications()).then((result) => {
            this.remindersChecklists = result;
            this.filterRemindersData();
        });*/

        setInterval(() => {
            this.ngZone.run(() => this.filterRemindersData());
        }, 20000);

        this.fiSignalRService.itemUpdatedEvent.subscribe((data) => {
            if (data.ObjectType === ObjectTypes.CheckListActivated && data.EventType === FiItemUpdatedEventType.Feed) {
                if (data.ActionType === ActionType.Delete) {
                    this.removeChecklistReminderItem(data.ObjectId);
                    this.filterRemindersData();
                } else {
                    // we can receive many NewsFeedUpdated events in a short amount of time
                    // so we should collect info about which objects we need to request backend
                    this.checkListUpdateData.push(data.ObjectId);
                    this.getCheckListRemindersDebounced();
                }
            }
        });

        //return promise;
    }

    private checkListUpdateData: number[] = [];
    private getCheckListRemindersDebounced = fiDebounce(() => {
        if (this.checkListUpdateData.length) {
            // we collected info about which objects we need to request backend and make request after 1000ms idle
            const currentCheckListUpdateData = this.checkListUpdateData;
            this.checkListUpdateData = [];

            const params = this.getParameterForSystemNotifications();
            params.CheckListBusinessUnitSettingIds = currentCheckListUpdateData;
            return this.checklistService.getCheckLists(params).then((items) => {
                currentCheckListUpdateData.forEach((currItemId) => {
                    const newItem = items.find((x) => x.BusinessUnitSettingId === currItemId);
                    if (!newItem) {
                        this.removeChecklistReminderItem(currItemId);
                    } else {
                        this.updateChecklistReminderItem(currItemId, newItem);
                    }
                });

                this.filterRemindersData();
            });
        }
    }, 1000);

    private removeChecklistReminderItem(objectId: number) {
        const currentItem = this.remindersChecklists.find((item) => item.BusinessUnitSettingId === objectId);
        if (currentItem) {
            this.remindersChecklists.splice(this.remindersChecklists.indexOf(currentItem), 1);
        }
    }
    private updateChecklistReminderItem(objectId: number, newItem: ChecklistModel) {
        const currentItem = this.remindersChecklists.find((item) => item.BusinessUnitSettingId === objectId);
        if (currentItem) {
            this.remindersChecklists[this.remindersChecklists.indexOf(currentItem)] = newItem;
        } else {
            this.remindersChecklists.push(newItem);
        }
    }

    private setCheckListRemindersCount(data) {
        this.ngZone.run(() => this.checkListRemindersCount$.next(data));
    }

    private filterRemindersData() {
        const prevChecklistIds = this.checkListReminders.map((item) => item.Id);

        const now = new Date();
        // show only with reached start date and not reached end date
        const currentChecklists = this.remindersChecklists.filter((checkList) => {
            const isStartDateOk = checkList.StartDateTime && now >= new Date(checkList.StartDateTime);
            const isDueDateOk = !checkList.DueDateTime || now < new Date(checkList.DueDateTime);
            return isStartDateOk && isDueDateOk;
        });
        currentChecklists.forEach((checklist) => {
            // checklists in system notifications area are always failed checklists. But until start date is reached
            // for checklist it has "NotFilledOut" status. Because of this we need manually set "Failed" status
            // for all checklists in system notifications
            checklist.AnswersStatus = ChecklistModel.Status.Failed;
            if (
                checklist.RemindDateTime &&
                now > new Date(checklist.RemindDateTime) &&
                checklist.IsTurnOffReminder !== true &&
                !(
                    checklist.PublicationSetting.PublicationType === PublicationType.Continous &&
                    checklist.TimeSetting === ChecklistModel.TimeSettings.OneTime
                )
            ) {
            }
        });
        this.setCheckListRemindersCount(currentChecklists.length);

        // https://chainformation.atlassian.net/browse/FI-2942
        // we should check if we have changed reminders
        let isDataChanged =
            currentChecklists.length !== prevChecklistIds.length ||
            !!currentChecklists.find((currentChecklist) => prevChecklistIds.indexOf(currentChecklist.Id) === -1);
        if (isDataChanged) {
            this._checkListReminders$.next(currentChecklists);
        }
    }

    private getParameterForSystemNotifications() {
        return {
            FilterType: SideListOptionType.CurrentNotCompleted,
            CheckListBusinessUnitSettingIds: undefined,
        };
    }
}
